import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { usePermission } from '../../../context/PermissionContext';
import {
  Menu, X, Server, Code, Database, Shield,
  Users, FolderPlus, Activity, Eye,
  FileCode, Play, Terminal, Settings,
  Table, HardDrive, Network, Lock
} from 'lucide-react';

const SystemConfigSidebar = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { hasPermission } = usePermission();

  const navigationItems = [
    {
      category: 'SFTP',
      items: [
        { name: 'User Management', path: '/tiles/system_configuration/sftp/users', icon: <Users className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Directory Management', path: '/tiles/system_configuration/sftp/directories', icon: <FolderPlus className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'IP Access Control', path: '/tiles/system_configuration/sftp/ip-control', icon: <Lock className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Server Status', path: '/tiles/system_configuration/sftp/status', icon: <Activity className="w-5 h-5" />, permission: 'system_configuration' }
      ]
    },
    {
      category: 'Scripts',
      items: [
        { name: 'Script Manager', path: '/tiles/system_configuration/scripts/manager', icon: <FileCode className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Execution Monitor', path: '/tiles/system_configuration/scripts/monitor', icon: <Play className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Log Viewer', path: '/tiles/system_configuration/scripts/logs', icon: <Terminal className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Settings', path: '/tiles/system_configuration/scripts/settings', icon: <Settings className="w-5 h-5" />, permission: 'system_configuration' }
      ]
    },
    {
      category: 'Database',
      items: [
        { name: 'Schema Manager', path: '/tiles/system_configuration/database/schema', icon: <Table className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Replication Setup', path: '/tiles/system_configuration/database/replication', icon: <HardDrive className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Query Console', path: '/tiles/system_configuration/database/console', icon: <Terminal className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Performance Monitor', path: '/tiles/system_configuration/database/performance', icon: <Activity className="w-5 h-5" />, permission: 'system_configuration' }
      ]
    },
    {
      category: 'Proxy',
      items: [
        { name: 'Configuration', path: '/tiles/system_configuration/proxy/config', icon: <Settings className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'SSL Certificates', path: '/tiles/system_configuration/proxy/ssl', icon: <Shield className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Load Balancing', path: '/tiles/system_configuration/proxy/load-balancing', icon: <Network className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'Access Logs', path: '/tiles/system_configuration/proxy/logs', icon: <Eye className="w-5 h-5" />, permission: 'system_configuration' },
        { name: 'VPN (WireGuard)', path: '/tiles/system_configuration/proxy/vpn', icon: <Lock className="w-5 h-5" />, permission: 'system_configuration' }
      ]
    }
  ];

  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <>
      <button
        className="lg:hidden fixed top-4 left-4 z-50 p-2 rounded-md bg-slate-800 text-slate-200"
        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
      >
        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
          className={`fixed inset-y-0 left-0 z-40 transition-transform duration-300 transform 
            ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
            h-screen bg-slate-800 border-r border-slate-700 w-64 overflow-y-auto`}
      >
        <div className="p-4 border-b border-slate-700/50">
          <Link 
            to="/"
            className="flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700/50 hover:text-slate-100 rounded-md"
          >
            <span className="mr-3">←</span>
            <span>Back to HQ</span>
          </Link>
        </div>

        <nav className="mt-4">
          {navigationItems.map((section) => {
            // Filter items based on permissions
            const permittedItems = section.items.filter(item => hasPermission(item.permission));
            
            // Only render the section if it has at least one permitted item
            if (permittedItems.length === 0) return null;
            
            return (
              <div key={section.category} className="mb-6">
                <h3 className="px-4 mb-2 text-xs font-semibold text-slate-400 uppercase tracking-wider">
                  {section.category}
                </h3>
                <div>
                  {permittedItems.map(item => (
                    <Link 
                      key={item.path} 
                      to={item.path}
                      className={`flex items-center px-4 py-2 text-sm ${
                        isActive(item.path)
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:bg-slate-700/50 hover:text-slate-100'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="mr-3">{item.icon}</span>
                      <span>{item.name}</span>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>
      </div>
    </>
  );
};

export default SystemConfigSidebar;